// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import type { StatusToken } from '../../../utils/entityHelpers';
import type { PowerFlowConductor } from '../../../utils/powerFlowRouting';
import type { PowerFlowInference } from '../../../utils/powerFlowInference';
import type { PowerFlowLiveState } from '../../../utils/powerFlowLive';
import { conductorStroke } from './conductors/conductorStyle';
import { BreakerMark } from './conductors/BreakerMark';
import {
  BREAKER_ARC_R,
  breakerTitle,
  placeBreakers,
  type BreakerPlacement,
} from '../../../utils/powerFlowBreakers';
import type { AlarmsByAsset } from '../../../utils/powerFlowAlarms';
import type { BreakerAlarmTarget } from './conductors/BreakerAlarmPopover';
import {
  conductorClasses,
  conductorStatus,
  paintConductors,
  type ConductorPaint,
} from './conductors/conductorPaint';

export { conductorClasses };

export interface SldEdgeLayerProps {
  conductors: PowerFlowConductor[];
  width: number;
  height: number;
  /** Asset id → status, used to colour a conductor by its worse end. */
  statusOf: Map<string, StatusToken>;
  /** The selection and its upstream feeders, or null for no selection. */
  connectedIds: Set<string> | null;
  /** Live inference: energization, flow, standby. Omitted draws structure only. */
  live?: PowerFlowLiveState;
  /** Structural inference: bypasses, risers, branch class. */
  inference?: PowerFlowInference;
  /** When false, only measured state is painted — no standby, bypass or flow. */
  showInferred?: boolean;
  /** Short name for a breaker id, for the mark's hover title. */
  labelOf?: (_id: string) => string;
  /** Asset id → open alarms, for the breaker marks' hover lines. */
  alarmsOf?: AlarmsByAsset;
  /** A breaker's ALARM mark was activated. */
  onAlarmClick?: (_target: BreakerAlarmTarget) => void;
  /**
   * Each conductor's live paint, when the caller has already derived it
   * (`paintConductors`); derived here otherwise. The stage passes its own so
   * the dots it pulses and the runs they sit on read one verdict.
   */
  paint?: ReadonlyMap<string, ConductorPaint>;
}

/**
 * All conductors, as one SVG overlaying the stage.
 *
 * One SVG rather than an element per conductor: the paths are already in stage
 * coordinates, so they inherit the stage's transform for free, and the browser
 * composites one node instead of hundreds.
 *
 * **The conductor palette is its own set of tokens** (`--sld-energized`,
 * `--sld-flow`, `--sld-tie`), not the status tokens, and red here means
 * *energized* rather than *faulted* — the convention both reference one-lines
 * draw in, adopted deliberately in ADR-0016.
 *
 * So a conductor's status never changes its stroke colour. It adds a halo
 * (`.sld-edge--warn` / `--fault` in the stylesheet), which reads as heavier and
 * brighter against the red rather than disappearing into it. The severity
 * itself is named by the shaped `StatusMark` on the asset at the end of the
 * run, not here. See ADR-0007 for the token rule and glossary §6 for the
 * reading of the reference palette.
 *
 * The live pass adds what the readings say: a de-energized or standby run is
 * dashed grey, a run carrying load gains the flow overlay, a bypass and the MV
 * riser are classed for the legend. None of it moves a path.
 *
 * The SVG is `aria-hidden` and non-interactive. Connectivity is not conveyed
 * to assistive technology by these paths: each asset's accessible name says
 * what it feeds, and the detail panel lists its connections. Breakers are
 * folded out of the node list, so they get their own accessible list beside
 * the drawing — each one's name, position and alarm, the same words as its
 * hover title.
 */
const SldEdgeLayerComponent = ({
  conductors,
  width,
  height,
  statusOf,
  connectedIds,
  live,
  inference,
  showInferred = true,
  labelOf,
  alarmsOf,
  onAlarmClick,
  paint,
}: SldEdgeLayerProps) => {
  const rawId = React.useId();
  const paintOf = React.useMemo(
    () => paint ?? paintConductors(conductors, live, inference, showInferred),
    [paint, conductors, live, inference, showInferred]
  );
  const paintFor = (conductor: PowerFlowConductor): ConductorPaint =>
    paintOf.get(conductor.id) ?? { classes: [], flowing: false };
  const maskId = `sld-breaker-cuts-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const isDimmed = (conductor: PowerFlowConductor) =>
    connectedIds !== null &&
    !(connectedIds.has(conductor.source) && connectedIds.has(conductor.target));
  const breakers = React.useMemo(() => {
    const placements = placeBreakers(conductors);
    return conductors
      .filter(conductor => placements.has(conductor.id))
      .map(conductor => ({ conductor, at: placements.get(conductor.id) as BreakerPlacement }));
  }, [conductors]);

  return (
    <>
      {breakers.length > 0 && (
        <ul className="sld-sr-only" aria-label="Circuit breakers">
          {breakers.map(({ conductor }) => (
            <li key={conductor.id} data-sld-breaker-entry={(conductor.via ?? []).join(' ')}>
              {breakerTitle(conductor.via ?? [], live?.breakerOf, labelOf)}
            </li>
          ))}
        </ul>
      )}
      <svg
        className="sld-edges"
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        aria-hidden="true"
        focusable="false"
      >
        {breakers.length > 0 && (
          <defs>
            <mask id={maskId} maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height}>
              <rect x={0} y={0} width={width} height={height} fill="white" />
              {breakers.map(({ conductor, at }) => (
                <rect
                  key={conductor.id}
                  x={at.horizontal ? at.x - BREAKER_ARC_R : at.x - 3}
                  y={at.horizontal ? at.y - 3 : at.y - BREAKER_ARC_R}
                  width={at.horizontal ? BREAKER_ARC_R * 2 : 6}
                  height={at.horizontal ? 6 : BREAKER_ARC_R * 2}
                  fill="black"
                />
              ))}
            </mask>
          </defs>
        )}
        <g mask={breakers.length > 0 ? `url(#${maskId})` : undefined}>
          {conductors.map(conductor => {
            const status = conductorStatus(conductor, statusOf);
            const dimmed =
              connectedIds !== null &&
              !(connectedIds.has(conductor.source) && connectedIds.has(conductor.target));

            const stroke = conductorStroke(conductor.voltageClass, conductor.route);
            const classes = ['sld-edge', `sld-edge--${conductor.route}`];
            if (dimmed) classes.push('is-dimmed');
            if (connectedIds !== null && !dimmed) classes.push('is-highlighted');
            if (status) classes.push(`sld-edge--${status}`);

            const liveClasses = paintFor(conductor);
            classes.push(...liveClasses.classes);

            return (
              <React.Fragment key={conductor.id}>
                <path
                  className={classes.join(' ')}
                  d={conductor.d}
                  fill="none"
                  strokeWidth={stroke.width}
                  strokeDasharray={stroke.dashArray}
                  strokeLinecap="butt"
                  strokeLinejoin="miter"
                  data-sld-edge={conductor.id}
                  data-sld-route={conductor.route}
                  data-sld-energized={live?.energizedOf.get(conductor.id) ?? undefined}
                />
                {liveClasses.flowing && (
                  <path
                    className={`sld-edge-flow${dimmed ? ' is-dimmed' : ''}`}
                    d={conductor.d}
                    fill="none"
                    strokeWidth={Math.max(stroke.width - 0.4, 1)}
                    strokeLinecap="butt"
                    strokeLinejoin="miter"
                    data-sld-flow={conductor.id}
                  />
                )}
              </React.Fragment>
            );
          })}
        </g>
        {breakers.map(({ conductor, at }) => (
          <BreakerMark
            key={`breaker:${conductor.id}`}
            conductor={conductor}
            at={at}
            breakerOf={live?.breakerOf}
            labelOf={labelOf}
            alarmsOf={alarmsOf}
            onAlarmClick={onAlarmClick}
            dimmed={isDimmed(conductor)}
            highlighted={connectedIds !== null && !isDimmed(conductor)}
            paint={paintFor(conductor).classes}
            flowing={paintFor(conductor).flowing}
          />
        ))}
      </svg>
    </>
  );
};

export const SldEdgeLayer = React.memo(SldEdgeLayerComponent);
